<template>
  <div class="dynamic-filters">
    <div
      v-if="!filters.length"
      class="text-center p-3 text-muted"
    >No filters added yet.</div>
    <div v-else>
      <div
        v-for="(filter, index) in filters"
        :key="index"
        class="d-flex bg-light p-3 mb-3 rounded align-items-center"
      >
        <div class="d-flex align-items-center">
          <AppButton
            v-if="!disabled"
            variant="danger"
            class="p-1 mr-2"
            @click="removeFilter(index)"
          >
            <AppIcon name="close" />
          </AppButton>
          <div
            class="flex-1"
            style="width: 100px"
          >
            <span v-if="index === 0">Where</span>
            <FormSelect
              v-else-if="index === 1"
              :disabled="disabled"
              class="pr-2"
              :value="condition"
              v-bind="getConditionSelectProps()"
              @input="updateCondition($event)"
            />
            <span v-else>{{ condition | capitalize }}</span>
          </div>
        </div>
        <DynamicFilter
          :value="filter"
          :view="view"
          :fields="fields"
          :types="types"
          :editable="!disabled"
          class="flex-1"
          @input="updateFilter(index,$event)"
        />
      </div>
    </div>
    <div
      v-if="!disabled"
      class="row no-gutters pt-3 mt-3 border-top"
    >
      <div class="col">
        <AppButton
          :disabled="disabled"
          data-test="addFilterBtn"
          class="w-100"
          @click="addFilter()"
        >
          <AppIcon
            name="add_circle"
            class="pr-2"
          />
          <span>Add filter</span>
        </AppButton>
      </div>
    </div>
  </div>
</template>

<script>
import DynamicFilter from './DynamicFilter'
import FormSelect from '@/components/Forms/FormSelect'
import _without from 'lodash/without'
import { INPUT_TYPES_TO_FILTER_TYPES_MAP } from '@/constants/'
import { toRefs } from '@vue/composition-api'
import useDynamicFilters from '@/components/composables/useDynamicFilters'

const DEFAULT_CONDITION = 'and'

export default {
  components: { DynamicFilter, FormSelect },
  props: {
    value: {
      type: Object,
      default: () => ({ filters: [], condition: DEFAULT_CONDITION }),
    },
    view: {
      type: String,
      default: 'em',
    },
    fields: {
      type: Array,
      default: () => [],
    },
    conditions: {
      type: Array,
      default: () => [ { key: 'and', label: 'And' }, { key: 'or', label: 'Or' } ],
    },
    types: {
      type: Object,
      default: () => INPUT_TYPES_TO_FILTER_TYPES_MAP,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  setup (props) {
    const { fields, types } = toRefs(props)
    const { getFilterById, fieldsFormatted } = useDynamicFilters(fields, types)

    return {
      getFilterById,
      fieldsFormatted,
    }
  },
  computed: {
    filters () {
      return (this.value.filters || []).map(el => ({
        ...el, id: el.id || `${el.name}-${el.group}`,
      }))
    },
    condition () {
      return this.value.condition || DEFAULT_CONDITION
    },
  },
  methods: {
    getConditionSelectProps () {
      return {
        trackBy: 'key',
        config: {
          options: this.conditions,
          trackBy: 'key',
          label: 'label',
        },
      }
    },
    updateCondition (update) {
      this.$emit('input', { ...this.value, condition: update })
    },
    addFilter () {
      const id = this.fieldsFormatted[0].id
      const updatedFilter = { id, ...(this.getFilterById(id) || {}) }
      this.updateFilter(this.filters.length, updatedFilter)
    },
    removeFilter (index) {
      this.$emit('input', { condition: this.condition, filters: _without(this.filters, this.filters[index]) })
    },
    updateFilter (index, updatedFilter) {
      const restFilters = _without(this.filters, this.filters[index])
      const [ front, tail ] = [ restFilters.slice(0, index), restFilters.slice(index) ]
      this.$emit('input', {
        condition: this.condition,
        filters: [
          ...front,
          updatedFilter,
          ...tail,
        ],
      })
    },
  },
}
</script>
